export type CookieConsent = 'accepted' | 'denied';

const STORAGE_KEY = 'gayphx_cookie_consent';
const COOKIE_NAME = 'gayphx_cookie_consent';
const MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

const listeners = new Set<() => void>();
let settingsRequested = false;

function readCookie(): CookieConsent | null {
  const match = document.cookie.match(/(?:^|; )gayphx_cookie_consent=(accepted|denied)(?:;|$)/);
  return match ? (match[1] as CookieConsent) : null;
}

export function getCookieConsent(): CookieConsent | null {
  try {
    const fromCookie = readCookie();
    if (fromCookie) return fromCookie;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'accepted' || stored === 'denied') return stored;
  } catch {
    return null;
  }
  return null;
}

export function setCookieConsent(value: CookieConsent) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${value}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* the cookie is the record that matters */
  }
  if (value === 'denied') {
    clearNonEssentialCookies();
  }
  settingsRequested = false;
  listeners.forEach((listener) => listener());
}

export function openCookieSettings() {
  settingsRequested = true;
  listeners.forEach((listener) => listener());
}

export function consumeCookieSettingsRequest() {
  const requested = settingsRequested;
  settingsRequested = false;
  return requested;
}

export function subscribeCookieConsent(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Drop analytics and ad cookies. The consent choice itself stays. */
export function clearNonEssentialCookies() {
  const host = window.location.hostname;
  const domains = ['', host, host.startsWith('www.') ? host.slice(4) : `.${host}`];
  const names = document.cookie
    .split(';')
    .map((part) => part.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name));

  for (const name of names) {
    if (name === COOKIE_NAME) continue;
    if (!isNonEssentialCookie(name)) continue;
    for (const domain of domains) {
      const domainAttr = domain ? `; Domain=${domain}` : '';
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${domainAttr}`;
    }
  }

  const gtag = window.gtag;
  if (typeof gtag === 'function') {
    gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
}

function isNonEssentialCookie(name: string) {
  return (
    name.startsWith('_ga') ||
    name.startsWith('_gid') ||
    name.startsWith('_gat') ||
    name.startsWith('_gcl') ||
    name === '_fbp' ||
    name === '_fbc'
  );
}
